<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useConnectionStore } from '@/modules/connecting/stores/connection-store'
import { useNotificationStore } from '@/modules/shared/stores/notification-store'
import { VButton } from '@/modules/shared/components'

const route = useRoute()
const router = useRouter()
const connectionStore = useConnectionStore()
const notificationStore = useNotificationStore()

const { connectionId, individualId } = route.params as {
  connectionId: string
  individualId: string
}

const loading = ref(true)
const submitting = ref(false)
const agreed = ref(false)
const agreement = ref<{ agreement_id: number; file_url: string; file_name: string } | null>(null)
const connection = computed(() => connectionStore.getConnection(connectionId))
const firmName = computed(() => connection.value?.candidate_group?.subject?.name || 'this firm')

onMounted(async () => {
  try {
    if (!connection.value) {
      await connectionStore.fetchConnections({ status: 'accepted', individual_id: individualId })
    }
    agreement.value = await connectionStore.fetchAgreement(connectionId)
  } catch {
    notificationStore.enqueue('error', 'Could not load the membership agreement.')
    router.push({ name: 'connecting.individuals', params: { slug: 'connections', individualId } })
    return
  } finally {
    loading.value = false
  }
})

const submit = async () => {
  if (!agreed.value) return
  submitting.value = true
  try {
    await connectionStore.acceptAgreement(connectionId)
    const subdomain = connection.value?.candidate_group?.subject?.subdomain
    if (subdomain) {
      const baseUrl = import.meta.env.VITE_VENTURE360_REDIRECT_URL
      window.location.href = `${baseUrl}/user?slug=${subdomain}`
    } else {
      router.push({ name: 'connecting.individuals', params: { slug: 'connections', individualId } })
    }
  } catch {
    notificationStore.enqueue('error', 'Could not accept the agreement. Please try again.')
    submitting.value = false
  }
}

const goBack = () => {
  router.push({ name: 'connecting.individuals', params: { slug: 'connections', individualId } })
}
</script>

<template>
  <div class="relative flex max-h-screen min-h-screen flex-col overflow-hidden bg-gray-50">
    <!-- Top bar -->
    <div class="pointer-events-none fixed inset-x-0 top-0 z-50">
      <div class="h-1 bg-[#009192]" />
      <div class="flex items-center justify-between px-6 pt-5">
        <img src="/venture360-logo.svg" class="h-6 w-auto" alt="Venture360" />
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex flex-1 items-center justify-center">
      <div class="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-[#009192]" />
    </div>

    <!-- Content -->
    <div v-else class="flex flex-1 justify-center overflow-y-auto [scrollbar-width:none]">
      <div class="flex w-full max-w-[600px] flex-col px-5 pt-[72px] max-sm:px-4 max-sm:pt-8">
        <div class="animate-[fadeUp_0.4s_cubic-bezier(0.22,1,0.36,1)_forwards]">
          <!-- Icon -->
          <div
            class="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl border border-[rgba(0,145,146,0.12)] bg-[linear-gradient(135deg,rgba(0,145,146,0.08),rgba(0,132,180,0.08))] text-[#009192]"
          >
            <svg
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              viewBox="0 0 24 24"
              stroke-linecap="round"
              stroke-linejoin="round"
              width="24"
              height="24"
            >
              <path
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>

          <h2 class="mb-2 text-[26px] font-bold leading-[1.2] tracking-tight text-gray-900 max-sm:text-[22px]">
            Membership Agreement
          </h2>
          <p class="mb-9 text-[15px] leading-[1.6] text-gray-500 [text-wrap:pretty]">
            {{ firmName }} requires you to review and accept their membership agreement before you can access their
            portal.
          </p>

          <!-- Document -->
          <div class="mb-8 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div class="flex items-center gap-3">
              <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500">
                <svg
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  viewBox="0 0 24 24"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
              </div>
              <div class="min-w-0 flex-1">
                <div class="truncate text-sm font-semibold text-gray-900">{{ agreement?.file_name }}</div>
                <a
                  :href="agreement?.file_url"
                  target="_blank"
                  class="text-sm font-medium text-[#009192] hover:underline"
                >
                  View document
                </a>
              </div>
            </div>
          </div>

          <!-- Checkbox -->
          <label class="mb-8 flex cursor-pointer items-start gap-3">
            <input
              v-model="agreed"
              type="checkbox"
              class="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-gray-300 text-[#009192] accent-[#009192]"
            />
            <span class="text-[15px] leading-[1.6] text-gray-700">
              I have read and agree to the membership agreement.
            </span>
          </label>
        </div>

        <div class="h-[90px] shrink-0" />
      </div>
    </div>

    <!-- Bottom bar -->
    <div
      v-if="!loading"
      class="fixed inset-x-0 bottom-0 z-40 flex items-center justify-between border-t border-gray-200/60 bg-white/80 px-6 py-4 backdrop-blur-[16px] [backdrop-filter:blur(16px)_saturate(1.5)]"
    >
      <button
        class="cursor-pointer border-none bg-transparent text-sm font-medium text-gray-500 hover:text-gray-700"
        @click="goBack"
      >
        Back
      </button>
      <VButton :disabled="!agreed || submitting" :loading="submitting" @click="submit"> Accept & Continue </VButton>
    </div>
  </div>
</template>

<style scoped>
@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
